import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Vote, ExternalLink, ShieldCheck, MapPin, CalendarDays, User, ArrowRight } from 'lucide-react';
import { STATE_VOTER_LINKS } from '../data/voterStates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: '', address: '', dob: '', state: '' };

export default function VoterRegistration() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const selectedState = useMemo(
    () => STATE_VOTER_LINKS.find((s) => s.code === form.state) || null,
    [form.state]
  );

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const persistOutreach = () => {
    // Fire-and-forget POST so the click handler can synchronously open a tab
    // (window.open after await is blocked by Safari/iOS pop-up blockers).
    // Re-persist for each action so a user editing a new voter after the first
    // save still gets recorded.
    axios
      .post(`${API}/voter-outreach`, {
        name: form.name.trim(),
        address: form.address.trim(),
        dob: form.dob,
        state_code: selectedState.code,
        state_name: selectedState.name,
      })
      .then(() => setSubmitted(true))
      .catch((err) => {
        // Non-fatal: user was still redirected to state site
        console.warn('Voter outreach save failed', err);
      });
  };

  const validate = () => {
    if (!form.name.trim()) { toast.error('Please enter your full name.'); return false; }
    if (!form.address.trim()) { toast.error('Please enter your address.'); return false; }
    if (!form.dob) { toast.error('Please enter your date of birth.'); return false; }
    if (!form.state) { toast.error('Please select your state.'); return false; }
    return true;
  };

  const handleAction = (kind) => {
    if (!validate()) return;
    const url = kind === 'register' ? selectedState.register : selectedState.status;
    // Open the tab synchronously inside the user gesture BEFORE any network
    // work, so pop-up blockers do not intercept it.
    window.open(url, '_blank', 'noopener,noreferrer');
    persistOutreach();
    toast.success(
      kind === 'register'
        ? `Opening ${selectedState.name}'s official voter registration portal…`
        : `Opening ${selectedState.name}'s official voter status lookup…`
    );
  };

  const resetForm = () => { setForm(initialForm); setSubmitted(false); };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fade-in-up" data-testid="voter-registration-page">
      {/* Hero */}
      <section className="banner-bg section-noise">
        <div className="max-w-5xl mx-auto px-4 py-14 text-center">
          <div className="chip mb-3 inline-flex items-center gap-2"><Vote size={13} /> YOUR VOICE • YOUR VOTE</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cbtu-cream">
            REGISTER TO <span className="gold-text-fill">VOTE</span>
          </h1>
          <p className="text-cbtu-text/90 mt-4 max-w-2xl mx-auto text-base">
            CBTU Region III is committed to civic engagement. Enter your details, choose your state,
            and we'll route you to your state's official portal to register or verify your registration.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 card-dark p-6 md:p-8 rounded-md">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-full bg-cbtu-green/50 border border-gold/40">
              <ShieldCheck className="text-gold" size={18} />
            </div>
            <div>
              <div className="chip mb-1">STEP 1</div>
              <h2 className="font-display text-2xl text-cbtu-cream">Your Information</h2>
            </div>
          </div>

          <div className="space-y-4">
            <label className="block" data-testid="voter-name-label">
              <span className="block text-xs tracking-widest text-gold font-heading mb-1 flex items-center gap-1.5">
                <User size={12} /> Full Name <span className="text-red-400">*</span>
              </span>
              <input
                data-testid="voter-name-input"
                type="text"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="Jane Q. Voter"
                required
                className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40"
              />
            </label>

            <label className="block" data-testid="voter-address-label">
              <span className="block text-xs tracking-widest text-gold font-heading mb-1 flex items-center gap-1.5">
                <MapPin size={12} /> Address <span className="text-red-400">*</span>
              </span>
              <input
                data-testid="voter-address-input"
                type="text"
                value={form.address}
                onChange={(e) => update({ address: e.target.value })}
                placeholder="123 Main St, City, ZIP"
                required
                className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40"
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block" data-testid="voter-dob-label">
                <span className="block text-xs tracking-widest text-gold font-heading mb-1 flex items-center gap-1.5">
                  <CalendarDays size={12} /> Date of Birth <span className="text-red-400">*</span>
                </span>
                <input
                  data-testid="voter-dob-input"
                  type="date"
                  value={form.dob}
                  onChange={(e) => update({ dob: e.target.value })}
                  max={today}
                  required
                  className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40"
                />
              </label>

              <label className="block" data-testid="voter-state-label">
                <span className="block text-xs tracking-widest text-gold font-heading mb-1">
                  State <span className="text-red-400">*</span>
                </span>
                <select
                  data-testid="voter-state-select"
                  value={form.state}
                  onChange={(e) => update({ state: e.target.value })}
                  required
                  className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40"
                >
                  <option value="">— Select your state —</option>
                  {STATE_VOTER_LINKS.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-8 pt-6 border-t border-gold/20">
            <div className="chip mb-3">STEP 2 — CHOOSE AN ACTION</div>
            <p className="text-cbtu-text/85 text-sm mb-4">
              {selectedState
                ? <>You'll be redirected to <span className="text-gold">{selectedState.name}'s</span> official portal to complete the process.</>
                : <>Once you select your state, you can register or verify your registration below.</>
              }
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleAction('register')}
                data-testid="voter-register-btn"
                className="gold-button px-5 py-3 rounded-sm inline-flex items-center justify-center gap-2"
              >
                <Vote size={16} /> Register to Vote <ExternalLink size={13} />
              </button>
              <button
                type="button"
                onClick={() => handleAction('status')}
                data-testid="voter-status-btn"
                className="ghost-button px-5 py-3 rounded-sm inline-flex items-center justify-center gap-2"
              >
                <ShieldCheck size={16} /> Check My Status <ExternalLink size={13} />
              </button>
            </div>
            {submitted && (
              <div className="mt-4 flex items-center justify-between gap-3 text-xs bg-cbtu-green/30 border border-gold/30 rounded-sm px-3 py-2">
                <span className="text-cbtu-cream">Outreach saved. Submit another voter?</span>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="voter-reset-btn"
                  className="text-gold hover:text-gold-bright font-heading tracking-widest inline-flex items-center gap-1"
                >
                  START NEW <ArrowRight size={12} />
                </button>
              </div>
            )}
          </div>

          <p className="text-cbtu-muted text-[11px] mt-6 leading-relaxed">
            <span className="text-gold font-heading tracking-widest">PRIVACY:</span> CBTU Region III does not
            collect your Social Security number, driver's license, or state ID. Your name, address, and DOB are
            saved only so our outreach team can follow up. The actual registration is completed on your state's
            official government website.
          </p>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="card-dark p-6 rounded-md">
            <div className="font-heading tracking-widest text-gold text-xs mb-3">WHY IT MATTERS</div>
            <p className="text-sm text-cbtu-cream leading-relaxed">
              Every ballot cast strengthens Black trade unionism and working families. The CBTU has led voter
              education, registration, and mobilization since 1972 — because power at the ballot box protects
              power at the bargaining table.
            </p>
          </div>

          <div className="card-dark p-6 rounded-md">
            <div className="font-heading tracking-widest text-gold text-xs mb-3">KEY DEADLINES</div>
            <ul className="space-y-2 text-sm text-cbtu-cream">
              <li className="flex gap-2"><span className="text-gold">★</span> Deadlines vary by state — most close 15–30 days before Election Day.</li>
              <li className="flex gap-2"><span className="text-gold">★</span> Many states allow same-day registration at your polling place.</li>
              <li className="flex gap-2"><span className="text-gold">★</span> Check your state's portal for the most accurate cutoffs.</li>
            </ul>
          </div>

          {selectedState && (
            <div className="card-dark p-6 rounded-md border-gold/50" data-testid="voter-state-links">
              <div className="font-heading tracking-widest text-gold text-xs mb-3">{selectedState.name.toUpperCase()} • OFFICIAL LINKS</div>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href={selectedState.register} target="_blank" rel="noopener noreferrer" className="text-cbtu-cream hover:text-gold-bright inline-flex items-center gap-1.5 break-all">
                    Register <ExternalLink size={12} className="text-gold flex-shrink-0" />
                  </a>
                </li>
                <li>
                  <a href={selectedState.status} target="_blank" rel="noopener noreferrer" className="text-cbtu-cream hover:text-gold-bright inline-flex items-center gap-1.5 break-all">
                    Check Status <ExternalLink size={12} className="text-gold flex-shrink-0" />
                  </a>
                </li>
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
